import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  createEvent,
  fetchEventInfo,
  updateEvent,
  updateEventInfo,
  uploadEventBackground,
  uploadEventLogo,
  uploadEventPrizeBackground,
  uploadEventSplash,
} from '../services/firebaseService'
import { DEFAULT_INFO_MARKDOWN } from '../constants/defaultInfoMarkdown'

// Every event field an admin can edit through the settings. A save writes the
// ones that differ from the stored document and nothing else, so a tab that
// edits three fields writes three fields. `validatorCodes` is deliberately not
// here: codes are added and removed on the spot by their own calls, never
// through a save.
export const EDITABLE_EVENT_FIELDS = [
  'name',
  'backgroundUrl', 'backgroundColor', 'logoUrl', 'prizeBackgroundUrl', 'prizeBackgroundColor', 'splashUrl',
  'colorBg', 'colorSurface', 'colorPrimary', 'colorText',
  'challenges', 'challengePrompt', 'maxVideosPerUser',
  'challengeMode', 'requireAuth', 'maxRejections', 'prizeConfig', 'prizePrompt',
  'maxSegmentDuration', 'maxRecordingDuration',
  'receptionEnabled', 'receptionStart', 'receptionEnd',
  'defaultModerated',
  'streamEnabled', 'galleryPublic',
  'tier',
]

// Clearing these is never what the admin meant — an event with no name, or a
// colour field emptied mid-typing — so an empty value keeps the stored one.
const KEEP_WHEN_EMPTY = new Set([
  'name', 'backgroundColor', 'prizeBackgroundColor', 'colorBg', 'colorSurface', 'colorPrimary', 'colorText',
])

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

// Which image field each upload slot fills, and the call that stores it.
const IMAGE_SLOTS = {
  background: { field: 'backgroundUrl', upload: uploadEventBackground },
  logo: { field: 'logoUrl', upload: uploadEventLogo },
  prizeBackground: { field: 'prizeBackgroundUrl', upload: uploadEventPrizeBackground },
  splash: { field: 'splashUrl', upload: uploadEventSplash },
}
const NO_FILES = { background: null, logo: null, prizeBackground: null, splash: null }

/**
 * The editable copy of one event: the draft, the image files waiting to be
 * uploaded, the info sheet's Markdown, whether any of it differs from what is
 * stored, and the save that writes the difference.
 *
 * `stored` is the event document as it is now (live, from a snapshot), or the
 * defaults a new event starts from when `creating`.
 */
export function useEventDraft({ eventId, stored, creating = false, lang }) {
  const [draft, setDraft] = useState(stored ?? null)
  const [files, setFiles] = useState(NO_FILES)
  const [saving, setSaving] = useState(false)

  const [infoOriginal, setInfoOriginal] = useState(null)
  const [infoMarkdown, setInfoMarkdown] = useState(null)
  const [infoLoading, setInfoLoading] = useState(!creating && !!eventId)

  // A snapshot can land while someone is mid-edit — a validator code being
  // generated writes the event document. Taking the new document wholesale
  // would wipe their edits, so fields they changed keep the draft's value and
  // everything else follows the store.
  const previous = useRef(stored)
  useEffect(() => {
    if (!stored) return
    const before = previous.current
    previous.current = stored
    setDraft((current) => {
      if (!current || !before) return stored
      const next = { ...stored }
      for (const key of EDITABLE_EVENT_FIELDS) {
        if (!same(current[key], before[key])) next[key] = current[key]
      }
      return next
    })
  }, [stored])

  useEffect(() => {
    if (creating || !eventId) return
    let cancelled = false
    setInfoLoading(true)
    fetchEventInfo(eventId)
      .then((data) => data?.markdown ?? DEFAULT_INFO_MARKDOWN)
      .catch((err) => {
        console.error('Failed to load event info', err)
        return DEFAULT_INFO_MARKDOWN
      })
      .then((md) => {
        if (cancelled) return
        setInfoOriginal(md)
        setInfoMarkdown(md)
      })
      .finally(() => { if (!cancelled) setInfoLoading(false) })
    return () => { cancelled = true }
  }, [eventId, creating])

  const changedFields = useMemo(() => {
    if (!draft || !stored) return []
    return EDITABLE_EVENT_FIELDS.filter((key) => !same(draft[key], stored[key]))
  }, [draft, stored])

  const infoChanged = infoOriginal !== null && infoMarkdown !== infoOriginal
  const filesChanged = Object.values(files).some(Boolean)
  const dirty = changedFields.length > 0 || infoChanged || filesChanged

  const setField = useCallback((key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
  }, [])

  const setFile = useCallback((slot, file) => {
    setFiles((prev) => ({ ...prev, [slot]: file }))
  }, [])

  const discard = useCallback(() => {
    setDraft(stored)
    setFiles(NO_FILES)
    if (infoOriginal !== null) setInfoMarkdown(infoOriginal)
  }, [stored, infoOriginal])

  // Throws on failure, so the caller decides how to report it and whether to
  // leave edit mode.
  const save = useCallback(async () => {
    setSaving(true)
    try {
      const withUploads = { ...draft }
      for (const [slot, { field, upload }] of Object.entries(IMAGE_SLOTS)) {
        if (files[slot]) withUploads[field] = await upload(eventId, files[slot])
      }

      if (creating) {
        await createEvent(withUploads, lang)
      } else {
        const updates = {}
        for (const key of EDITABLE_EVENT_FIELDS) {
          const value = withUploads[key]
          if (value === undefined) continue
          if (KEEP_WHEN_EMPTY.has(key) && !value) continue
          if (!same(value, stored?.[key])) updates[key] = value
        }
        if (Object.keys(updates).length > 0) await updateEvent(eventId, updates)
        if (infoChanged) {
          await updateEventInfo(eventId, infoMarkdown)
          setInfoOriginal(infoMarkdown)
        }
      }
      setFiles(NO_FILES)
    } finally {
      setSaving(false)
    }
  }, [draft, files, creating, lang, eventId, stored, infoChanged, infoMarkdown])

  return {
    draft,
    setDraft,
    setField,
    files,
    setFile,
    info: { markdown: infoMarkdown, setMarkdown: setInfoMarkdown, loading: infoLoading },
    changedFields,
    dirty,
    saving,
    save,
    discard,
  }
}
